<template>
  <f7-page name="shop-create">
    <f7-navbar back-link :title="$t('shopPage.createShop')">
      <f7-nav-right>
        <span class="step-indicator">{{ step }}/3</span>
      </f7-nav-right>
    </f7-navbar>
    <AppLayout>
      <template #main>


        <div class="step-bar">
          <div class="step-fill" :style="{ width: `${(step / 3) * 100}%` }"></div>
        </div>

        <!-- Step 1: Basic Info -->
        <f7-page-content v-if="step === 1">
          <div class="padding">
            <div class="logo-upload" role="button" tabindex="0" @click="pickLogo" @keydown.enter="pickLogo" @keydown.space.prevent="pickLogo">
              <img v-if="logoPreview" :src="logoPreview" class="logo-img" />
              <div v-else class="logo-placeholder">
                <f7-icon f7="camera_plus" size="22" color="#1A6B42" />
                <div class="logo-label">{{ $t('shopPage.shopLogo') }}</div>
              </div>
            </div>

            <f7-list inset strong media-list dividers outline>
              <f7-list-input
                v-model:value="form.name"
                :label="$t('shop.shopName')"
                type="text"
                :placeholder="$t('shopPage.shopNamePlaceholder')"
                required
                validate
                maxlength="60"
                clear-button
              />
              <f7-list-item
                :title="form.category || $t('shop.categorie')"
                smart-select
                :smart-select-params="{ openIn: 'popup', searchbar: true, closeOnSelect: true }"
              >
                <select v-model="form.categoryId" required>
                  <option v-for="cat in categories" :key="cat.id" :value="cat.id">
                    {{ cat.name }}
                  </option>
                </select>
              </f7-list-item>
              <f7-list-input
                v-model:value="form.description"
                :label="$t('shop.description')"
                type="textarea"
                :placeholder="$t('shopPage.shopDescPlaceholder')"
                required
                validate
                maxlength="500"
                resizable
              />
              <f7-list-input
                v-model:value="form.location"
                :label="$t('shopPage.location')"
                type="text"
                :placeholder="$t('shopPage.locationPlaceholder')"
              />
              <f7-list-input
                ref="phoneInputRef"
                v-model:value="form.contactPhone"
                :label="$t('shopPage.whatsappNumber')"
                type="tel"
                placeholder="+225 07 XX XX XX XX"
              />
            </f7-list>

            <div class="section-title">{{ $t('shopPage.shopVisibility') }}</div>

            <f7-card
              class="vis-card"
              :class="{ 'vis-card--selected': form.visibility === 'PUBLIC' }"
              :padding="false"
              role="radio"
              :aria-checked="form.visibility === 'PUBLIC'"
              tabindex="0"
              @click="form.visibility = 'PUBLIC'"
              @keydown.enter="form.visibility = 'PUBLIC'"
              @keydown.space.prevent="form.visibility = 'PUBLIC'"
            >
              <f7-card-content class="vis-card-content" :padding="false">
                <div class="vis-icon green">
                  <f7-icon f7="globe" size="16" color="#27500A" />
                </div>
                <div class="vis-body">
                  <div class="vis-title">{{ $t('shop.boutiquePublique') }}</div>
                  <div class="vis-desc">{{ $t('shopPage.publicShopDesc') }}</div>
                </div>
                <f7-icon v-if="form.visibility === 'PUBLIC'" f7="check" size="16" color="#1A6B42" />
              </f7-card-content>
            </f7-card>

            <f7-card
              class="vis-card"
              :class="{ 'vis-card--selected': form.visibility !== 'PUBLIC' }"
              :padding="false"
              role="radio"
              :aria-checked="form.visibility !== 'PUBLIC'"
              tabindex="0"
              @click="form.visibility = 'MEMBERS_ONLY'"
              @keydown.enter="form.visibility = 'MEMBERS_ONLY'"
              @keydown.space.prevent="form.visibility = 'MEMBERS_ONLY'"
            >
              <f7-card-content class="vis-card-content" :padding="false">
                <div class="vis-icon blue">
                  <f7-icon f7="lock" size="16" color="#0C447C" />
                </div>
                <div class="vis-body">
                  <div class="vis-title">{{ $t('shop.boutiquePriveeMembres') }}</div>
                  <div class="vis-desc">{{ $t('shopPage.privateShopDesc') }}</div>
                </div>
                <f7-icon v-if="form.visibility !== 'PUBLIC'" f7="check" size="16" color="#1A6B42" />
              </f7-card-content>
            </f7-card>

            <f7-button large fill round raised @click="step = 2">
              {{ $t('shopPage.continueProducts') }}
            </f7-button>
          </div>
        </f7-page-content>

        <!-- Step 2: Private Shop Access (only if private) -->
        <f7-page-content v-else-if="step === 2 && form.visibility !== 'PUBLIC'">
          <div class="padding">
            <div class="info-banner blue">
              <f7-icon f7="lock" size="15" color="#0C447C" />
              <div>{{ $t('shopPage.privateShopBanner') }}</div>
            </div>

            <div class="section-title">{{ $t('shopPage.whoCanAccess') }}</div>

            <div class="toggle-row">
              <div class="toggle-left">
                <f7-icon f7="person_3" size="15" color="#1A6B42" />
                <div>
                  <div class="toggle-title">{{ $t('shopPage.myGroups') }}</div>
                  <div class="toggle-desc">{{ $t('shopPage.existingGroupMembers') }}</div>
                </div>
              </div>
              <f7-toggle :checked="form.groupsEnabled" @change="form.groupsEnabled = $event.target.checked" />
            </div>

            <div class="toggle-row">
              <div class="toggle-left">
                <f7-icon f7="link" size="15" color="#185FA5" />
                <div>
                  <div class="toggle-title">{{ $t('shopPage.inviteLink') }}</div>
                  <div class="toggle-desc">{{ $t('shopPage.sharedLinkAccess') }}</div>
                </div>
              </div>
              <f7-toggle :checked="form.inviteLinkEnabled" @change="form.inviteLinkEnabled = $event.target.checked" />
            </div>

            <div class="toggle-row">
              <div class="toggle-left">
                <f7-icon f7="key" size="15" color="#633806" />
                <div>
                  <div class="toggle-title">{{ $t('shop.codeAcces') }}</div>
                  <div class="toggle-desc">{{ $t('shopPage.secretCodeProtect') }}</div>
                </div>
              </div>
              <f7-toggle :checked="form.accessCodeEnabled" @change="form.accessCodeEnabled = $event.target.checked" />
            </div>

            <div v-if="form.accessCodeEnabled" class="padding-top">
              <f7-list inset strong>
                <f7-list-input
                  v-model:value="form.accessCode"
                  :label="$t('shop.codeAcces')"
                  type="text"
                  :placeholder="$t('shopPage.accessCodePlaceholder')"
                  maxlength="20"
                />
              </f7-list>
            </div>

            <div class="section-title">{{ $t('shopPage.memberPricing') }}</div>

            <div class="toggle-row">
              <div class="toggle-left">
                <div>
                  <div class="toggle-title">{{ $t('shopPage.activateMemberPricing') }}</div>
                  <div class="toggle-desc">{{ $t('shopPage.memberPriceDesc') }}</div>
                </div>
              </div>
              <f7-toggle :checked="form.memberPricingEnabled" @change="form.memberPricingEnabled = $event.target.checked" />
            </div>

            <div v-if="form.memberPricingEnabled" class="discount-row">
              <div class="discount-col">
                <div class="discount-label">{{ $t('shopPage.memberDiscount') }}</div>
                <div class="discount-value green">{{ form.memberDiscount }}%</div>
              </div>
              <div class="discount-col">
                <div class="discount-label">{{ $t('shopPage.groupBuyDiscount') }}</div>
                <div class="discount-value blue">{{ form.groupBuyDiscount }}%</div>
              </div>
            </div>

            <f7-button large fill round raised @click="step = 3">
              {{ $t('shopPage.continueCustomization') }}
            </f7-button>
          </div>
        </f7-page-content>

        <!-- Step 2 skip for public shops -->
        <f7-page-content v-else-if="step === 2 && form.visibility === 'PUBLIC'">
          <div class="padding">
            <f7-button large fill round raised @click="step = 3" class="margin-top">
              {{ $t('shopPage.continueCustomization') }}
            </f7-button>
          </div>
        </f7-page-content>

        <!-- Step 3: Customization -->
        <f7-page-content v-else-if="step === 3">
          <div class="padding">
            <div class="section-title">{{ $t('shopPage.shopBanner') }}</div>
            <div class="banner-upload" role="button" tabindex="0" @click="pickBanner" @keydown.enter="pickBanner" @keydown.space.prevent="pickBanner">
              <img v-if="bannerPreview" :src="bannerPreview" class="banner-img" />
              <div v-else class="banner-placeholder">
                <f7-icon f7="photo" size="18" color="#1A6B42" />
                <div>{{ $t('shopPage.addBanner') }}</div>
              </div>
            </div>

            <div class="section-title">{{ $t('shopPage.deliveryOptions') }}</div>

            <div class="toggle-row compact">
              <div class="toggle-left">
                <f7-icon f7="truck" size="14" color="#1A6B42" />
                <div class="toggle-title">{{ $t('shop.livraisonLocale') }}</div>
              </div>
              <f7-toggle :checked="form.deliveryLocal" @change="form.deliveryLocal = $event.target.checked" />
            </div>

            <div class="toggle-row compact">
              <div class="toggle-left">
                <f7-icon f7="walk" size="14" color="#185FA5" />
                <div class="toggle-title">{{ $t('shop.retraitBoutique') }}</div>
              </div>
              <f7-toggle :checked="form.deliveryPickup" @change="form.deliveryPickup = $event.target.checked" />
            </div>

            <div class="toggle-row compact">
              <div class="toggle-left">
                <f7-icon f7="world" size="14" color="#633806" />
                <div class="toggle-title">{{ $t('shop.livraisonNationale') }}</div>
              </div>
              <f7-toggle :checked="form.deliveryNational" @change="form.deliveryNational = $event.target.checked" />
            </div>

            <div class="section-title">{{ $t('shopPage.acceptedPayments') }}</div>
            <div class="payment-chips">
              <f7-button
                v-for="method in paymentMethods"
                :key="method.code"
                fill
                round
                raised
                small
                class="chip"
                :class="{ 'chip--active': form.paymentMethods.includes(method.code) }"
                @click="togglePayment(method.code)"
              >
                {{ method.label }}
              </f7-button>
            </div>

            <div class="section-title">{{ $t('shopPage.shopPolicy') }}</div>

            <div class="toggle-row compact">
              <div class="toggle-left">
                <div class="toggle-title">{{ $t('shopPage.returnsAccepted') }}</div>
              </div>
              <f7-toggle :checked="form.returnsAccepted" @change="form.returnsAccepted = $event.target.checked" />
            </div>

            <div class="toggle-row compact">
              <div class="toggle-left">
                <div class="toggle-title">{{ $t('shop.negociationPrix') }}</div>
              </div>
              <f7-toggle :checked="form.negotiationEnabled" @change="form.negotiationEnabled = $event.target.checked" />
            </div>

            <div class="toggle-row compact">
              <div class="toggle-left">
                <div class="toggle-title">{{ $t('shopPage.showInFeed') }}</div>
              </div>
              <f7-toggle :checked="form.showInFeed" @change="form.showInFeed = $event.target.checked" />
            </div>

            <f7-button large fill round raised :disabled="creating" @click="createShop">
              {{ creating ? $t('shop.creerEnCours') : $t('shop.creerBoutique') }}
            </f7-button>
          </div>
        </f7-page-content>
      </template>
    </AppLayout>
  </f7-page>
</template>

<script setup>
import { ref, reactive, computed } from 'vue'
import AppLayout from '@/components/common/AppLayout.vue'
import { f7 } from 'framework7-vue'
import { useShopStore } from '@/js/pinia-stores/shop.js'
import { useAuthStore } from '@/js/pinia-stores/auth.js'
import { useTelephone } from '@/js/composables/useTelephone.js'
import i18n from '@/js/i18n.js'

defineProps({ f7route: Object, f7router: Object })

const shopStore = useShopStore()
const authStore = useAuthStore()

const step = ref(1)
const creating = ref(false)
const logoPreview = ref('')
const bannerPreview = ref('')
const phoneInputRef = ref(null)
useTelephone(phoneInputRef)
const { t } = i18n.global

const categories = ref([
  { id: 'fashion', name: 'Mode & textile' },
  { id: 'food', name: 'Alimentation' },
  { id: 'craft', name: 'Artisanat' },
  { id: 'health', name: 'Santé & beauté' },
  { id: 'electronics', name: 'Électronique' },
  { id: 'home', name: 'Maison & déco' },
  { id: 'services', name: 'Services' },
  { id: 'other', name: 'Autre' },
])

const paymentMethods = [
  { code: 'AVO_WALLET', label: 'Avo Wallet' },
  { code: 'WAVE', label: 'Wave' },
  { code: 'ORANGE_MONEY', label: 'Orange Money' },
  { code: 'MTN_MOMO', label: 'MTN MoMo' },
  { code: 'CARD', label: 'Carte bancaire' },
]

const form = reactive({
  name: '',
  categoryId: '',
  description: '',
  location: '',
  contactPhone: '',
  contactEmail: '',
  visibility: 'PUBLIC',
  groupsEnabled: true,
  inviteLinkEnabled: true,
  accessCodeEnabled: false,
  accessCode: '',
  memberPricingEnabled: false,
  memberDiscount: 15,
  groupBuyDiscount: 25,
  deliveryLocal: true,
  deliveryPickup: true,
  deliveryNational: false,
  paymentMethods: ['AVO_WALLET', 'WAVE', 'ORANGE_MONEY'],
  returnsAccepted: true,
  negotiationEnabled: true,
  showInFeed: true,
})

const selectedCategoryName = computed(() => {
  const cat = categories.value.find((c) => c.id === form.categoryId)
  return cat?.name || ''
})

function togglePayment(code) {
  const idx = form.paymentMethods.indexOf(code)
  if (idx === -1) form.paymentMethods.push(code)
  else form.paymentMethods.splice(idx, 1)
}

function pickLogo() {
  const input = document.createElement('input')
  input.type = 'file'
  input.accept = 'image/*'
  input.onchange = (e) => {
    const file = e.target.files[0]
    if (file) logoPreview.value = URL.createObjectURL(file)
  }
  input.click()
}

function pickBanner() {
  const input = document.createElement('input')
  input.type = 'file'
  input.accept = 'image/*'
  input.onchange = (e) => {
    const file = e.target.files[0]
    if (file) bannerPreview.value = URL.createObjectURL(file)
  }
  input.click()
}

async function createShop() {
  if (!form.name || form.name.length < 3) {
    f7.dialog.alert(t('shopPage.nameMinChars3'));
    return
  }
  creating.value = true
  try {
    const payload = {
      name: form.name,
      description: form.description,
      location: form.location,
      contactPhone: form.contactPhone,
      contactEmail: form.contactEmail,
      visibility: form.visibility,
      logoUrl: logoPreview.value || '',
      bannerUrl: bannerPreview.value || '',
    }
    const created = await shopStore.createShop(payload)
    f7.toast.show({ text: t('shop.boutiqueCreee'), closeTimeout: 2000 })
    f7router.navigate('/sell')
  } catch (err) {
    f7.dialog.alert(err?.response?.data?.message || t('shop.erreurCreation'))
  } finally {
    creating.value = false
  }
}
</script>

<style scoped>
.step-indicator { font-size: 12px; color: #1A6B42; font-weight: 500; }
.step-bar { height: 3px; background: var(--f7-input-invalid-border-color, #e0e0e0); padding: 0 16px; margin-bottom: 8px; }
.step-fill { height: 100%; background: #C9A84C; border-radius: 2px; transition: width 0.3s; }
.logo-upload {
  width: 80px; height: 80px; border-radius: 12px; margin: 0 auto 12px;
  background: #EAF3DE; border: 1.5px dashed #97C459; display: flex; align-items: center;
  justify-content: center; overflow: hidden;
}
.logo-img { width: 100%; height: 100%; object-fit: cover; }
.logo-placeholder { text-align: center; }
.logo-label { font-size: 10px; color: #3B6D11; margin-top: 2px; }
.section-title { font-size: 11px; font-weight: 500; text-transform: uppercase; letter-spacing: 0.05em; color: var(--f7-on-surface-variant, #8e8e93); padding: 12px 16px 6px; }
.vis-card {
  margin: 0 16px 8px;
  box-shadow: none;
  transition: box-shadow 0.2s ease, transform 0.2s ease, border-color 0.2s ease;
}
.vis-card--selected {
  border-color: #1A6B42;
  box-shadow: 0 2px 8px rgba(26, 107, 66, 0.15);
}
.vis-card-content {
  display: flex; align-items: flex-start; gap: 10px; padding: 10px;
}
.vis-icon { width: 32px; height: 32px; border-radius: 8px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.vis-icon.green { background: #EAF3DE; }
.vis-icon.blue { background: #EBF0FC; }
.vis-body { flex: 1; }
.vis-title { font-size: 12px; font-weight: 500; margin-bottom: 2px; }
.vis-desc { font-size: 10px; color: var(--f7-on-surface-variant, #8e8e93); line-height: 1.4; }
.info-banner {
  display: flex; align-items: flex-start; gap: 8px; padding: 10px 12px;
  border-radius: 9px; margin: 0 16px 12px; font-size: 11px; line-height: 1.45;
}
.info-banner.blue { background: #EBF0FC; color: #0C447C; }
.toggle-row {
  display: flex; align-items: center; justify-content: space-between; padding: 10px 16px;
  border-bottom: 0.5px solid var(--f7-input-border-color, #e0e0e0);
}
.toggle-row.compact { padding: 8px 16px; }
.toggle-left { display: flex; align-items: center; gap: 10px; flex: 1; }
.toggle-title { font-size: 12px; font-weight: 500; }
.toggle-desc { font-size: 10px; color: var(--f7-on-surface-variant, #8e8e93); }
.discount-row { display: flex; gap: 8px; padding: 8px 16px; }
.discount-col { flex: 1; }
.discount-label { font-size: 11px; color: var(--f7-on-surface-variant, #8e8e93); }
.discount-value { font-size: 14px; font-weight: 600; margin-top: 2px; }
.discount-value.green { color: #1A6B42; }
.discount-value.blue { color: #185FA5; }
.banner-upload {
  height: 56px; border-radius: 9px; margin: 0 16px 12px;
  background: #EAF3DE; border: 1px dashed #97C459; display: flex; align-items: center;
  justify-content: center; overflow: hidden;
}
.banner-img { width: 100%; height: 100%; object-fit: cover; }
.banner-placeholder { display: flex; align-items: center; gap: 6px; font-size: 11px; color: #3B6D11; }
.payment-chips { display: flex; gap: 6px; flex-wrap: wrap; padding: 0 16px 8px; }
.chip {
  padding: 5px 12px; border-radius: 14px; font-size: 11px; font-weight: 500;
  background: var(--f7-list-bg-color, #f5f5f5); color: var(--f7-list-item-text-color, #333);
  border: 0.5px solid var(--f7-input-border-color, var(--f7-list-item-border-color, #e0e0e0));
}
.chip--active { background: #EAF3DE; color: #27500A; border: 1.5px solid #1A6B42; }
</style>
